import { useState } from "react";
import { NavLink } from "react-router-dom";
import { activeTrail, firstLeafPath, isNavGroup, navItemKey } from "../../config/navConfig";
import * as Icons from "./Icons";

/**
 * Левое меню (правка №52, Чат 2). [Claude] 2026-11-03
 * layout «top» — как в Битрикс24: слева разделы и группы одной строкой; клик по группе открывает
 * её первый раздел, а разделы группы показываются сверху строкой (TopSubmenu).
 * layout «side» — группы раскрываются здесь же, до двух уровней.
 * Раскрытые группы запоминаются у пользователя (prefs.open); группа с открытой страницей раскрыта сама.
 */

const linkClass = (active, nested) => (nested
    ? `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
        active ? "bg-yellow-50 text-yellow-800 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
    : `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
        active
            ? "bg-yellow-50 text-yellow-800 font-semibold shadow-sm border border-yellow-100/50"
            : "hover:bg-slate-50 text-slate-600 hover:text-slate-900"}`);

function Chevron({ opened }) {
    return (
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${opened ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    );
}

export default function SidebarMenu({ items, layout, pathname, open, onToggleOpen }) {
    const trail = activeTrail(items, pathname);
    const activeLeaf = trail[trail.length - 1];
    const openSet = new Set(open || []);
    const [closedHere, setClosedHere] = useState(() => new Set());

    const isOpen = (key) => openSet.has(key) || (trail.includes(key) && !closedHere.has(key));
    const toggle = (key) => {
        const opened = isOpen(key);
        setClosedHere((prev) => {
            const next = new Set(prev);
            if (opened) next.add(key);
            else next.delete(key);
            return next;
        });
        const nextOpen = opened ? [...openSet].filter((k) => k !== key) : [...openSet, key];
        if (onToggleOpen) onToggleOpen(nextOpen);
    };

    const renderLeaf = (item, nested) => {
        const Icon = Icons[item.icon] || Icons.FileTextIcon;
        return (
            <NavLink to={item.to} key={item.to} className={() => linkClass(activeLeaf === item.to, nested)}>
                {!nested && <Icon className="w-5 h-5 opacity-80" />}
                <span className="text-sm">{item.label}</span>
            </NavLink>
        );
    };

    const renderSideGroup = (item, depth) => {
        const key = navItemKey(item);
        const Icon = Icons[item.icon] || Icons.FileTextIcon;
        const opened = isOpen(key);
        const nested = depth > 0;
        return (
            <div key={key} className={nested ? "space-y-0.5" : "space-y-1"}>
                <button
                    type="button"
                    onClick={() => toggle(key)}
                    className={`w-full flex items-center gap-3 ${nested ? "px-3 py-1.5 rounded-lg" : "px-3 py-2.5 rounded-xl"} text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all ${
                        trail.includes(key) && !opened ? "text-yellow-800 font-semibold" : ""}`}
                    data-testid={`nav-group-${item.label}`}
                    aria-expanded={opened}
                >
                    {!nested && <Icon className="w-5 h-5 opacity-80" />}
                    <span className={`text-sm flex-1 text-left ${nested ? "" : "font-medium"}`}>{item.label}</span>
                    <Chevron opened={opened} />
                </button>
                {opened && (
                    <div className="ml-3 pl-3 border-l border-slate-100 space-y-0.5">
                        {item.children.map((child) => (isNavGroup(child) ? renderSideGroup(child, depth + 1) : renderLeaf(child, true)))}
                    </div>
                )}
            </div>
        );
    };

    const renderTopGroup = (item) => {
        const key = navItemKey(item);
        const Icon = Icons[item.icon] || Icons.FileTextIcon;
        const to = firstLeafPath(item);
        return (
            <NavLink
                to={to}
                key={key}
                className={() => linkClass(trail[0] === key, false)}
                data-testid={`nav-group-${item.label}`}
                title={`${item.label}: ${item.children.map((c) => c.label).join(", ")}`}
            >
                <Icon className="w-5 h-5 opacity-80" />
                <span className="text-sm flex-1">{item.label}</span>
            </NavLink>
        );
    };

    return (
        <nav className="flex flex-col space-y-1 mb-6" data-testid="sidebar-menu" data-layout={layout}>
            {items.map((item) => {
                if (!isNavGroup(item)) return renderLeaf(item, false);
                return layout === "side" ? renderSideGroup(item, 0) : renderTopGroup(item);
            })}
        </nav>
    );
}
